import { StandardInput } from "@/components/ui/forms/standardInput"
import { SwitchInlineInput } from "@/components/ui/forms/switchInlineInput"
import { TextAreaInput } from "@/components/ui/forms/textareaInput"
import { type SimpleFormik } from "@/typings/formik"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { ProgramPriceCard } from "@modules/programs/components/programPriceCard"
import { ProgramSharedCoachForm } from "@modules/programs/components/programSharedCoachForm"
import { UserProgramDraftEntity } from "@modules/programs/entities/userProgramDraftEntity"
import { type ProgramInfiniteInputKey } from "@modules/programs/types/programFormikType"
import { type ProgramOnOffInputKey } from "@modules/programs/types/programOnOffFormikType"
import { ProgramStatus, ProgramType } from "@modules/programs/types/programTypes"
import { CircleAlert } from "lucide-react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramInfoFormPros = {
  program: UserProgramDraftEntity
  isEditForm: boolean
  getFormErrorMessage: (
    name: ProgramInfiniteInputKey | ProgramOnOffInputKey,
    errorMessage?: string,
  ) => React.JSX.Element | undefined
  formik: SimpleFormik<UserProgramDraftEntity, ProgramInfiniteInputKey | ProgramOnOffInputKey>
} & React.ComponentProps<"div">

export function ProgramInfoForm({ program, isEditForm, formik, getFormErrorMessage, ...props }: ProgramInfoFormPros) {
  const isOnOffProgram = program.type === ProgramType.ONOFF
  const isProProgram = program.type === ProgramType.PRO

  return (
    <div className="flex flex-col gap-4" {...props}>
      <StandardInput
        label={word("program.form.title.label")}
        name="name"
        isRequiredInput
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        error={getFormErrorMessage("name")}
        inputProps={{
          placeholder: word("program.form.title.placeholder"),
        }}
      />

      <TextAreaInput
        label={word("program.form.description.label")}
        name="description"
        isRequiredInput
        getFieldProps={formik.getFieldProps}
        setFieldValue={formik.setFieldValue}
        error={getFormErrorMessage("description")}
        textareaProps={{
          className: "field-sizing-fixed",
          rows: 5,
        }}
      />

      {isOnOffProgram && (
        <>
          <StandardInput
            label={word("program.onOff.formik.label")}
            isRequiredInput
            getFieldProps={formik.getFieldProps}
            name="duration"
            error={getFormErrorMessage("duration")}
            setFieldValue={formik.setFieldValue}
            inputProps={{
              type: "number",
              inputMode: "numeric",
              step: 1,
              maxLength: 365,
              min: 0,
              placeholder: word("program.onOff.formik.placeholder"),
              disabled: program.status === ProgramStatus.PUBLISHED,
              onChange: (e) => {
                void formik.setFieldValue("duration", parseFloat(e.target.value))
              },
            }}
          />
          <div className="flex flex-row items-center gap-2">
            <WarningHexagonalIcon color="var(--color-neutral-600)" width={24} height={24} />
            <Text fontStyle="BS">
              {program.status === ProgramStatus.PUBLISHED
                ? word("program.onOff.durationEditPublished")
                : word("program.onOff.durationEditWarning")}
            </Text>
          </div>
        </>
      )}

      {isEditForm && program.id && (
        <>
          <div style={{ marginBottom: 7 }}>
            <Text fontStyle="LS" className="text-textDefault-caption">
              {word("program.price.label").toUpperCase()}
            </Text>
          </div>
          <ProgramPriceCard programId={program.id} editionAllowed={true} />
        </>
      )}

      {isEditForm && "visibility" in formik.values && (
        <SwitchInlineInput
          label={word("program.form.visibility.label")}
          name="visibility"
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("visibility")}
          setFieldValue={formik.setFieldValue}
          textSwitch={word("program.form.visibility.textSwitch")}
          containerClassName="flex gap-2"
        >
          <Text fontStyle="BS" className="px-5 text-justify leading-5 text-textDefault-caption">
            {word("program.form.visibility.description")}
          </Text>
        </SwitchInlineInput>
      )}

      {isProProgram && (
        <div className="card banner flex items-center gap-4" style={{ padding: "24px 28px" }}>
          <CircleAlert
            color="currentColor"
            className="shrink-0 rounded-full bg-surface-primary text-textDefault-body ring-4 ring-surface-primary"
          />
          <Text className="z-2" fontStyle="BM" style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}>
            {word("program.form.pro.information_about_fields_application")}
          </Text>
        </div>
      )}

      {!isOnOffProgram && "defaultHideSessionContent" in formik.values && (
        <SwitchInlineInput
          label={word("program.form.hideSessionContent.label")}
          textSwitch={word("program.form.hideSessionContent.textSwitch")}
          name="defaultHideSessionContent"
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("defaultHideSessionContent")}
          setFieldValue={formik.setFieldValue}
          containerClassName="flex gap-2"
        >
          <Text fontStyle="BS" className="px-5 text-justify leading-5 text-textDefault-caption">
            {word("program.form.hideSessionContent.description")}
          </Text>
        </SwitchInlineInput>
      )}
      {"entranceCheckEnabled" in formik.values && !isProProgram && (
        <SwitchInlineInput
          textSwitch={word("program.form.sasActivation.textSwitch")}
          label={word("program.form.sasActivation.label")}
          name="entranceCheckEnabled"
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("entranceCheckEnabled")}
          setFieldValue={formik.setFieldValue}
          containerClassName="flex gap-2"
        >
          <Text fontStyle="BS" className="px-5 text-justify leading-5 text-textDefault-caption">
            {word("program.form.sasActivation.description")}
          </Text>
        </SwitchInlineInput>
      )}
      {!isOnOffProgram && "scoreboardLocked" in formik.values && (
        <SwitchInlineInput
          label={word("program.form.hideLeaderboard.label")}
          textSwitch={word("program.form.hideLeaderboard.textSwitch")}
          name="scoreboardLocked"
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("scoreboardLocked")}
          setFieldValue={formik.setFieldValue}
          containerClassName="flex gap-2"
        >
          <Text fontStyle="BS" className="px-5 text-justify leading-5 text-textDefault-caption">
            {word("program.form.hideLeaderboard.description")}
          </Text>
        </SwitchInlineInput>
      )}
      {"sharedCoaches" in formik.values && (
        <ProgramSharedCoachForm setFieldValue={formik.setFieldValue} getFieldProps={formik.getFieldProps} />
      )}
    </div>
  )
}
